/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {BooleanSelectionList} from "@app/models/boolean-selection-list";
import {TrecApiError} from "@trec/api/errors";
import {ReasonerOnDemandPrescription, ReasonerWarningPrescription} from "@trec/api/reasoner";
import {Feature} from "@trec/api/registry";
import {ErrorAction, StoreAction} from "@trec/components/store";
import {Goal} from "../models/goal";
import {Microlearning} from "../models/microlearning";
import {PersonalExperiment} from "../models/personal-experiments";

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace TpAdvActions {
  // App features ///////////////////////////////////////////////////////////////////////////////////

  export class FetchAppFeatures extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Fetch app features`;
  }

  export class UpdateAppFeatures extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Update app features`;

    constructor(public payload: {appFeatures: BooleanSelectionList<Feature>}) {
      super();
    }
  }

  // Personal experiments ///////////////////////////////////////////////////////////////////////////

  export class FetchPersonalExperiments extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Fetch personal experiments`;
  }

  export class UpdatePersonalExperiments extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Update personal experiments`;

    constructor(public payload: {selectedPes: PersonalExperiment[]}) {
      super();
    }
  }

  // Goals //////////////////////////////////////////////////////////////////////////////////////////

  export class FetchGoals extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Fetch goals`;
  }

  export class UpdateGoals extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Update goals`;

    constructor(public payload: {selectedGoals: Goal[]}) {
      super();
    }
  }

  // On-demand //////////////////////////////////////////////////////////////////////////////////////

  export class FetchOnDemandDialogs extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Fetch on-demand dialogs`;
  }

  export class UpdateOnDemandDialogs extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Update on-demand dialogs`;

    constructor(public payload: {prescription: ReasonerOnDemandPrescription; isNew?: boolean}) {
      super();
    }
  }

  // Microlearning //////////////////////////////////////////////////////////////////////////////////

  export class FetchMicrolearning extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Fetch microlearning`;
  }

  export class UpdateMicrolearning extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Update microlearning`;

    constructor(public payload: {selectedMls: Microlearning[]}) {
      super();
    }
  }

  // Warnings patient ///////////////////////////////////////////////////////////////////////////////

  export class FetchWarningsPatient extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Fetch warnings patient`;
  }

  export class UpdateWarningsPatient extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Update warnings patient`;

    constructor(
      public payload: {
        insert: ReasonerWarningPrescription[];
        remove: ReasonerWarningPrescription[];
      }
    ) {
      super();
    }
  }

  export class TpAdvError extends ErrorAction {
    static readonly type = `[Treatment Plan Adv] Error`;
    readonly error: true = true;

    constructor(public payload: TrecApiError) {
      super();
    }
  }

  // Clear status ///////////////////////////////////////////////////////////////////////////////////

  export class ClearStatus extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Clear status`;
  }

  /**
   * lista esami pdta
   */
  export class FetchEsamiPdtaList extends StoreAction {
    static readonly type = `[Treatment Plan Adv] Fetch esami pdta`;

    constructor(public payload: {trecUserId: string, context: string}) {
      super();
    }
  }
}
